import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import client from '../../../lib/feathers';
import Page from '../../Page';
import Breadcrumb from '../../Breadcrumb';

/**
 * One section of a listing, on its own.
 *
 * The listing edit form is a single ~1,700-line page covering profile, address,
 * contact, amenities and hours, with one submit at the bottom. That is a lot to
 * put in front of a winery whose checklist just asked them to check one thing —
 * "confirm your hours" should not open everything they own.
 *
 * These sections PATCH only the keys they are responsible for, which is better
 * than the big form does: feathers-sequelize implements PUT literally and nulls
 * every column absent from the payload, so the whole-listing save has to send
 * every field back on every save to avoid wiping them. A PATCH cannot do that.
 * It also means `confirm-changed-listing-fields` sees a small, honest diff and
 * ticks off exactly what the winery touched.
 */
export default function SectionEditForm({
  listing,
  title,
  description,
  children,
  buildPatch,
  onSaved,
  backTo,
}) {
  const navigate = useNavigate();
  const user = useSelector((st) => st.auth.user);
  // The same forms serve admin and industry. Each writes through its own scope,
  // so a winery's save still runs the industry hooks — ownership, the field
  // whitelist, confirmation stamping — and an admin's correctly does not.
  const role = user?.role === 'admin' ? 'admin' : 'industry';
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const patch = buildPatch();
      const updated = await client.service(`${role}/listing`).patch(listing.id, patch);
      toast.success('Saved');
      onSaved?.(updated);
      if (backTo) navigate(backTo);
    } catch (err) {
      toast.error(err.message || 'Could not save that.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      {/* Without this a section form is a floating card: no listing name, no way
          back, no sense of where you are. The whole-listing form has always had
          a breadcrumb; these need the same. */}
      <div className="pb-5 border-b border-gray-200">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb
            elements={[
              { name: 'Listing Detail', href: `/${role}/listings/${listing.id}/` },
              { name: `${listing.name}: ${title}` },
            ]}
          />
        </nav>
      </div>

      <form onSubmit={save} className="bg-white shadow px-4 py-5 sm:rounded-lg my-5">
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <h3 className="text-lg font-medium leading-6 text-gray-900">{title}</h3>
          {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
        </div>
        <div className="mt-5 md:col-span-2 md:mt-0 space-y-4">
          {children}
        </div>
      </div>

      <div className="mt-6 pt-5 border-t border-gray-200 flex justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate(backTo || -1)}
          className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 text-sm rounded-md text-white bg-red-800 hover:bg-red-900 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      </form>
    </Page>
  );
}
